import { JoinNow } from './JoinNow';
import { Stats } from './Stats';
import { useEffect, useRef, useState } from 'react';
import { Competition } from 'functions/src/types/firestore/Game/Competition';
import { LeaderboardContainer } from './LeaderboardContainer';
import { ResultRanked } from '../tournaments/heats-leaderboards/HeatLeaderboard';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import { GradientTypography } from '../gradients/GradientTypography';
import { rankedResultsColumns } from '../tournaments/heats-leaderboards/rankedResultsColumns';
import { HeatTop3 } from '../tournaments/heats-leaderboards/HeatTop3';

export type LeaderboardUndeadBlocksProps = Pick<
  Competition<Date>,
  | 'title'
  | 'prize'
  | 'blurb'
  | 'date'
  | 'endDate'
  | 'sponsors'
  | 'scoreOption'
  | 'endpoint'
> & {
  statsEndpoint?: string;
};

export function LeaderboardUndeadBlocks({
  title,
  prize: joinNowPrize,
  blurb,
  date,
  endDate,
  sponsors,
  scoreOption,
  statsEndpoint,
  endpoint,
}: LeaderboardUndeadBlocksProps) {
  const [endpointData, setEndpointData] = useState<ResultRanked[]>([]);
  const [page, setPage] = useState(0);
  const dataToUse = endpointData;
  const [pageSize, setPageSize] = useState(20);
  const [apiIndex, setApiIndex] = useState(0);
  const gridHeight = pageSize * 52 + 130;
  //there are 50 data per number for the api endpoint
  useEffect(() => {
    setApiIndex((prev) => {
      return Math.max(prev, Math.floor(((page + 1) * pageSize + 1) / 50));
    });
  }, [page, pageSize]);
  const resultsLength = endpointData.length;
  const columns = [
    ...rankedResultsColumns({
      ...scoreOption?.[0],
      resultsLength,
    }),
  ];
  useEffect(() => {
    const handler = async () => {
      const response = await fetch(`${endpoint}/?number=${apiIndex}`);
      const jsonData = await response.json();
      const leaderboard = jsonData.leaderBoard;
      setEndpointData((prev) => {
        const newData = leaderboard.map((item) => {
          const { username, id, rank, score, pfp, prize } = item;
          return {
            name: username,
            id: id,
            prize: { currency: 'GZBUX', amount: prize },
            rank: rank,
            score: score,
            imgUrl: pfp,
          };
        });
        return [...prev, ...newData];
      });
    };
    if (endpoint) {
      handler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, apiIndex]);

  const ref = useRef<HTMLElement>();
  useEffect(() => {
    if (ref && ref.current) {
      ref.current.scrollIntoView();
    }
  }, [ref]);
  const [endpointStats, setEndpointsStats] = useState<
    { title: string; amount: number }[]
  >([]);
  const statsToUse = endpointStats;
  useEffect(() => {
    if (!statsEndpoint) {
      return;
    }
    const handler = async () => {
      //TODO: update when we know what stats endpoint response looks like
      const request = await fetch(statsEndpoint);
      const result = await request.json();
      setEndpointsStats(result);
    };
    handler();
  }, [statsEndpoint]);
  return (
    <LeaderboardContainer
      {...{ title, date, endDate, sponsors }}
      RightChildren={
        <>
          <HeatTop3
            top3Results={dataToUse.slice(0, 3)}
            scoreOption={scoreOption?.[0]}
          />

          <GradientTypography
            gradientColor="primary.vertical"
            variant="h5"
            sx={{ mb: 1, ml: 3 }}
          >
            LEADERBOARD
          </GradientTypography>
          <Box sx={{ height: `${gridHeight}px` }}>
            <DataGrid
              rows={dataToUse}
              getRowId={(row) => {
                return `${row.userIdCaptain}-${row.id}`;
              }}
              columns={columns}
              pagination
              page={page}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              onPageChange={setPage}
              disableColumnFilter
              disableColumnMenu
              disableSelectionOnClick
            />
          </Box>
        </>
      }
    >
      <>
        {endDate.getTime() - new Date().getTime() > 0 && (
          <JoinNow blurb={blurb} prize={joinNowPrize} />
        )}
        {statsToUse.length > 0 ? <Stats stats={statsToUse} /> : ''}
      </>
    </LeaderboardContainer>
  );
}
